import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { fmtPrice, fmtKm, waLink, SITE } from "@/lib/site";

const TAN = 0.079;

export const CarDetailModal = ({ car, onClose }) => {
  const [anticipo, setAnticipo] = useState(0);
  const [mesi, setMesi] = useState(48);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (car) {
      setAnticipo(Math.round((car.price * 0.2) / 100) * 100);
      setMesi(48);
      setActive(0);
      setLightbox(false);
    }
  }, [car]);

  if (!car) return null;

  const images = (car.images && car.images.length ? car.images : [car.image]).filter(Boolean);

  const prev = () => setActive((a) => (a - 1 + images.length) % images.length);
  const next = () => setActive((a) => (a + 1) % images.length);

  const amount = Math.max(car.price - anticipo, 0);
  const r = TAN / 12;
  const rata = amount > 0 ? (amount * r) / (1 - Math.pow(1 + r, -mesi)) : 0;

  const carLabel = `${car.brand} ${car.model}`;

  const specs = [
    ["Anno", car.year],
    ["Chilometri", fmtKm(car.km)],
    ["Alimentazione", car.fuel],
    ["Cambio", car.transmission],
    ["Potenza", car.power],
    ["Stato", car.category],
  ];

  const arrowBtn =
    "absolute top-1/2 -translate-y-1/2 p-2.5 rounded-full glass border border-border text-gray-200 hover:text-amber-500 hover:border-amber-500/50 transition-colors z-10";

  return (
    <Dialog open={!!car} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-[#111622] border-border text-gray-100 max-w-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="car-detail-modal"
        onPointerDownOutside={(e) => lightbox && e.preventDefault()}
        onInteractOutside={(e) => lightbox && e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (lightbox) {
            e.preventDefault();
            setLightbox(false);
          }
        }}
      >
        <DialogTitle className="sr-only">{carLabel} — dettagli veicolo</DialogTitle>
        <DialogDescription className="sr-only">
          Galleria fotografica, scheda tecnica e simulatore di finanziamento per {carLabel}
        </DialogDescription>
        <div className="relative h-64 sm:h-80 bg-[#0D1119] group/gallery">
          <button
            data-testid="car-detail-image-open"
            onClick={() => setLightbox(true)}
            className="absolute inset-0 w-full h-full cursor-zoom-in"
            aria-label="Vedi foto a schermo intero"
          >
            <img
              src={images[active]}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40 scale-110"
            />
            <img
              src={images[active]}
              alt={`${carLabel} - foto ${active + 1}`}
              className="relative w-full h-full object-contain"
              data-testid="car-detail-main-image"
            />
          </button>
          <div className="absolute inset-0 bg-gradient-to-t from-[#111622] to-transparent pointer-events-none" />
          <span className="absolute top-4 right-16 p-2 rounded-full glass border border-border text-gray-400 opacity-0 group-hover/gallery:opacity-100 transition-opacity pointer-events-none">
            <Maximize2 className="w-4 h-4" />
          </span>
          <button
            data-testid="car-detail-close"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full glass border border-border text-gray-300 hover:text-amber-500 z-10"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
          {images.length > 1 && (
            <>
              <button data-testid="car-gallery-prev" onClick={prev} className={`${arrowBtn} left-3`} aria-label="Foto precedente">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="car-gallery-next" onClick={next} className={`${arrowBtn} right-3`} aria-label="Foto successiva">
                <ChevronRight className="w-5 h-5" />
              </button>
              <span
                className="absolute top-4 left-4 glass border border-border text-[11px] font-mono2 tracking-widest text-gray-300 px-3 py-1 rounded-full"
                data-testid="car-detail-photo-counter"
              >
                {active + 1} / {images.length}
              </span>
            </>
          )}
          <div className="absolute bottom-4 left-6 pointer-events-none">
            <p className="font-mono2 text-xs uppercase tracking-widest text-amber-500">{car.brand} · {car.category}</p>
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">{car.model}</h3>
          </div>
        </div>

        {images.length > 1 && (
          <div className="flex gap-2 px-6 sm:px-8 pt-4 overflow-x-auto" data-testid="car-detail-thumbnails">
            {images.map((src, i) => (
              <button
                key={i}
                data-testid={`car-gallery-thumbnail-${i}`}
                onClick={() => setActive(i)}
                className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border transition-all ${
                  i === active ? "border-amber-500 ring-1 ring-amber-500/50" : "border-border opacity-60 hover:opacity-100"
                }`}
                aria-label={`Foto ${i + 1}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-6 sm:p-8 grid gap-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {specs.map(([k, v]) => (
              <div key={k} className="bg-[#161D2B] border border-border rounded-lg p-3.5">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gray-500">{k}</p>
                <p className="mt-1 font-semibold text-sm">{v}</p>
              </div>
            ))}
          </div>

          {car.description && (
            <p className="text-gray-400 text-sm leading-relaxed" data-testid="car-detail-description">{car.description}</p>
          )}

          <div className="bg-[#161D2B] border border-amber-500/20 rounded-xl p-5" data-testid="finance-calculator">
            <p className="font-mono2 text-[11px] uppercase tracking-widest text-amber-500 mb-4">
              Simula il finanziamento · TAN indicativo 7,9%
            </p>
            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Anticipo</label>
                <input
                  data-testid="finance-downpayment-input"
                  type="number"
                  min="0"
                  step="500"
                  value={anticipo}
                  onChange={(e) => setAnticipo(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full bg-[#0D1119] border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500/60"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Durata</label>
                <select
                  data-testid="finance-duration-select"
                  value={mesi}
                  onChange={(e) => setMesi(Number(e.target.value))}
                  className="w-full bg-[#0D1119] border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500/60"
                >
                  {[24, 36, 48, 60, 72].map((m) => (
                    <option key={m} value={m}>{m} mesi</option>
                  ))}
                </select>
              </div>
              <div className="bg-[#0D1119] border border-border rounded-lg px-4 py-2.5">
                <p className="text-xs text-gray-500">Rata stimata</p>
                <p className="font-display text-2xl font-extrabold text-amber-500" data-testid="finance-monthly-rate">
                  {fmtPrice(Math.round(rata))}<span className="text-sm text-gray-500 font-normal">/mese</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <p className="font-display text-3xl font-extrabold text-gray-100 sm:mr-auto" data-testid="car-detail-price">
              {fmtPrice(car.price)}
            </p>
            <a
              data-testid="car-detail-whatsapp-btn"
              href={waLink(`Buongiorno ${SITE.shortName}, vorrei maggiori informazioni sulla ${carLabel} (${fmtPrice(car.price)}).`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3.5 rounded-full transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Chiedi Info su WhatsApp
            </a>
          </div>
        </div>
      </DialogContent>

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center pointer-events-auto"
            data-testid="photo-lightbox"
            onClick={() => setLightbox(false)}
          >
            <img
              src={images[active]}
              alt={`${carLabel} - foto ${active + 1} a schermo intero`}
              className="max-w-[92vw] max-h-[88vh] object-contain"
              data-testid="lightbox-image"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(false)}
              className="absolute top-5 right-5 p-3 rounded-full glass border border-border text-gray-200 hover:text-amber-500 transition-colors"
              aria-label="Chiudi foto"
            >
              <X className="w-5 h-5" />
            </button>
            {images.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full glass border border-border text-gray-200 hover:text-amber-500 hover:border-amber-500/50 transition-colors"
                  aria-label="Foto precedente"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  data-testid="lightbox-next"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full glass border border-border text-gray-200 hover:text-amber-500 hover:border-amber-500/50 transition-colors"
                  aria-label="Foto successiva"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <span
                  className="absolute bottom-6 left-1/2 -translate-x-1/2 glass border border-border text-xs font-mono2 tracking-widest text-gray-300 px-4 py-1.5 rounded-full"
                  data-testid="lightbox-counter"
                >
                  {active + 1} / {images.length}
                </span>
              </>
            )}
          </div>,
          document.body
        )}
    </Dialog>
  );
};
